import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { RegistryRecordFrame } from '../RegistryRecordFrame';
import { CareerTimeline } from './contact/CareerTimeline';
import { ContactHeader } from './contact/ContactHeader';
import { useContactHearsay } from './contact/ContactHearsay';
import { contactRail } from './contact/contactRail';
import { useContactDocuments, useContactDossier, useContactTouchpoints } from './contact/hooks';
import { LogTouchpointDialog, logTouchpointTargets } from './contact/LogTouchpointDialog';
import { TouchpointsList } from './contact/TouchpointsList';
import { WhereTheyAppear } from './contact/WhereTheyAppear';
/**
 * Person (contact) record page, REG-11. Route /registries/contacts/:id behind
 * registries_records_v1 (REG-08). Reads the REG-10 dossier contract; never
 * renders email, phone or rates.
 */ export function ContactRecordPage() {
    const { id = '' } = useParams();
    const { user } = useAuth();
    const tenantId = user?.tenant_id ?? '';
    const dossierQuery = useContactDossier(tenantId, id);
    const dossier = dossierQuery.data ?? null;
    const touchpointsQuery = useContactTouchpoints(tenantId, id, !!dossier);
    const docsQuery = useContactDocuments(tenantId, id, !!dossier);
    const [logOpen, setLogOpen] = useState(false);
    // REG-02/03: hearsay about this person (rail) + "Add hearsay" (header).
    const hearsay = useContactHearsay(dossier ? dossier.id : '', dossier?.name ?? '');
    const status = dossierQuery.isPending ? 'loading' : dossierQuery.isError ? 'error' : dossier ? 'ready' : 'unavailable';
    const targets = dossier ? logTouchpointTargets(dossier.appearances) : [];
    const touchpoints = touchpointsQuery.data?.pages.flatMap((p)=>p.items) ?? [];
    const visibleTotal = touchpointsQuery.data?.pages[0]?.visibleTotal ?? dossier?.visibleTouchpointCount ?? 0;
    return /*#__PURE__*/ _jsx(RegistryRecordFrame, {
        kindLabel: "Contacts",
        tab: "contacts",
        title: dossier?.name,
        status: status,
        error: dossierQuery.error,
        onRetry: ()=>void dossierQuery.refetch(),
        header: dossier && /*#__PURE__*/ _jsx(ContactHeader, {
            dossier: dossier,
            onLogTouchpoint: targets.length > 0 ? ()=>setLogOpen(true) : undefined,
            extraActions: hearsay.addAction
        }),
        railSections: dossier ? contactRail(dossier, docsQuery.data, {
            claims: hearsay.claims
        }) : undefined,
        children: dossier && /*#__PURE__*/ _jsxs("div", {
            className: "space-y-8",
            "data-testid": Testids.registryPerson.page,
            children: [
                /*#__PURE__*/ _jsx(WhereTheyAppear, {
                    cards: dossier.appearances
                }),
                /*#__PURE__*/ _jsx(CareerTimeline, {
                    career: dossier.career
                }),
                /*#__PURE__*/ _jsx(TouchpointsList, {
                    touchpoints: touchpoints,
                    visibleTotal: visibleTotal,
                    hasMore: !!touchpointsQuery.hasNextPage,
                    loadingMore: touchpointsQuery.isFetchingNextPage,
                    onShowMore: ()=>void touchpointsQuery.fetchNextPage()
                }),
                /*#__PURE__*/ _jsx(LogTouchpointDialog, {
                    open: logOpen,
                    onOpenChange: setLogOpen,
                    contactId: dossier.id,
                    targets: targets
                }),
                hearsay.dialog
            ]
        })
    });
}
